import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, listRows, updateRow } from "@/lib/adminApi";
import {
  PAGE_STATUS,
  PAGE_TYPES,
  metaFor,
  optionsFrom,
  type WebsitePage,
} from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnGhost,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminWebsitePages() {
  const [rows, setRows] = useState<WebsitePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<WebsitePage>("website_pages", "title"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load website pages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id: number, patch: Partial<WebsitePage>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("website_pages", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const remove = async (id: number) => {
    const current = rows.find((row) => row.id === id);
    setRows((list) => list.filter((row) => row.id !== id));
    setSelectedId(null);
    try {
      await deleteRow("website_pages", id);
      notify(`${current?.title || "Page"} deleted.`);
    } catch {
      setError("We couldn't delete that page. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (typeFilter !== "all" && row.page_type !== typeFilter) return false;
      if (!query) return true;
      return [row.title, row.slug || "", row.author || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter, typeFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const totalViews = rows.reduce((sum, row) => sum + Number(row.views || 0), 0);

  const columns: Column<WebsitePage>[] = [
    {
      key: "title",
      header: "Page",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.title}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.slug || "—"}</p>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap capitalize">
          {row.page_type || "page"}
        </span>
      ),
    },
    { key: "sections", header: "Sections", render: (row) => <span className="text-sm text-foreground-700">{row.sections}</span> },
    { key: "views", header: "Views", render: (row) => <span className="text-sm text-foreground-700">{row.views.toLocaleString("en-IN")}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(PAGE_STATUS, row.status)} /> },
    { key: "updated", header: "Updated", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.updated_at)}</span> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Website Pages"
        subtitle="Every public page on the site — its type, publication state, sections, SEO and traffic."
      />

      <StatCards
        stats={[
          { label: "Total pages", value: rows.length, icon: "ri-pages-line", tone: "primary" },
          { label: "Published", value: rows.filter((row) => row.status === "published").length, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Drafts & review", value: rows.filter((row) => row.status === "draft" || row.status === "review").length, icon: "ri-draft-line", tone: "accent" },
          { label: "Total views", value: totalViews.toLocaleString("en-IN"), icon: "ri-eye-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search pages by title, slug, author…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(PAGE_STATUS)]} />
          <Select label="Type" value={typeFilter} onChange={setTypeFilter} options={[{ value: "all", label: "All types" }, ...PAGE_TYPES.map((type) => ({ value: type, label: type }))]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} pages</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading pages…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-pages-line" title={rows.length === 0 ? "No pages yet" : "No pages match"} message="Website pages will appear here." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelectedId(null)} title={selected?.title || "Page"} subtitle="Website page" footer={selected ? (
        <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
          <i className="ri-delete-bin-line"></i> Delete page
        </button>
      ) : null}>
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(PAGE_STATUS, selected.status)} />
              <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full capitalize">{selected.page_type || "page"}</span>
            </div>

            <DetailList
              items={[
                { icon: "ri-link", label: "Slug", value: selected.slug || "—" },
                { icon: "ri-layout-masonry-line", label: "Sections", value: `${selected.sections}` },
                { icon: "ri-eye-line", label: "Views", value: selected.views.toLocaleString("en-IN") },
                { icon: "ri-user-line", label: "Author", value: selected.author || "—" },
                { icon: "ri-calendar-line", label: "Last updated", value: formatDate(selected.updated_at) },
              ]}
            />

            {selected.seo_title ? (
              <Field label="SEO title">
                <p className="text-sm text-foreground-700">{selected.seo_title}</p>
              </Field>
            ) : null}
            {selected.seo_description ? (
              <Field label="Meta description">
                <p className="text-sm text-foreground-700 leading-relaxed">{selected.seo_description}</p>
              </Field>
            ) : null}

            <Field label="Publication status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value }, "Page status updated.")}
                className={fieldInput}
              >
                {optionsFrom(PAGE_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}